import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/base/ArticleCard";
import { useArticles } from "@/hooks/useArticles";
import { countries, sectors } from "@/mocks/articles";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

const PAGE_CONFIG: Record<string, { titleKey: string; subtitleKey: string; category: string; accent: string }> = {
  "/success-stories":      { titleKey: "listing.successStories.title", subtitleKey: "listing.successStories.subtitle", category: "Success Story",       accent: "orange" },
  "/startups":             { titleKey: "listing.startups.title",        subtitleKey: "listing.startups.subtitle",        category: "Startup",             accent: "green"  },
  "/innovations":          { titleKey: "listing.innovations.title",     subtitleKey: "listing.innovations.subtitle",     category: "Innovation",          accent: "yellow" },
  "/femmes-entrepreneures":{ titleKey: "listing.femmes.title",          subtitleKey: "listing.femmes.subtitle",          category: "Femme Entrepreneuse", accent: "pink"   },
  "/jeunes-talents":       { titleKey: "listing.jeunesTalents.title",   subtitleKey: "listing.jeunesTalents.subtitle",   category: "Jeune Talent",        accent: "purple" },
  "/diaspora-africaine":   { titleKey: "listing.diaspora.title",        subtitleKey: "listing.diaspora.subtitle",        category: "Diaspora Africaine",  accent: "teal"   },
};

const accentBg: Record<string, string> = {
  orange: "from-orange-500 to-orange-400",
  green:  "from-green-600 to-green-500",
  yellow: "from-yellow-500 to-yellow-400",
  pink:   "from-pink-500 to-pink-400",
  purple: "from-purple-600 to-purple-500",
  teal:   "from-gray-900 to-gray-800",
};

export default function ListingPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const config = PAGE_CONFIG[location.pathname] || PAGE_CONFIG["/success-stories"];
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

  const allCountriesLabel = t("listing.allCountries");
  const allSectorsLabel   = t("listing.allSectors");

  const [search, setSearch]           = useState("");
  const [country, setCountry]         = useState(allCountriesLabel);
  const [sector, setSector]           = useState(allSectorsLabel);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const title    = t(config.titleKey);
  const subtitle = t(config.subtitleKey);

  useSeoMeta({
    title: `${title} — Wazi Stories Afrique`,
    description: subtitle,
    type: "website",
    keywords: `${config.category}, entrepreneur africain, Afrique, Wazi Stories, ${title}`,
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${title} — Wazi Stories Afrique`,
    "description": subtitle,
    "url": `${siteUrl}${location.pathname}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Wazi Stories Afrique",
      "url": siteUrl
    }
  }, `listing-${location.pathname.replace(/\//g, "-")}`);

  const { articles, loading } = useArticles({
    category: config.category,
    country:  country !== allCountriesLabel ? country : undefined,
    sector:   sector  !== allSectorsLabel   ? sector  : undefined,
    search:   search || undefined,
  });

  const isFiltered = country !== allCountriesLabel || sector !== allSectorsLabel;

  const resetFilters = () => {
    setCountry(allCountriesLabel);
    setSector(allSectorsLabel);
  };

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* Hero banner */}
      <div className={`pt-20 bg-gradient-to-r ${accentBg[config.accent]} py-10 md:py-16`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h1 className="text-2xl md:text-5xl font-bold text-white font-serif mb-2 md:mb-3">{title}</h1>
          <p className="text-white/80 text-sm md:text-lg max-w-2xl mx-auto">{subtitle}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 md:p-5 mb-6 md:mb-8">
          {/* Search always visible */}
          <div className="relative mb-3 md:mb-0">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("listing.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>

          {/* Toggle filters on mobile */}
          <div className="flex items-center justify-between mt-3 md:hidden">
            <span className="text-xs text-gray-500">
              {isFiltered ? t("listing.activeFilters") : t("listing.filterHint")}
            </span>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-orange-500 cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              {filtersOpen ? t("listing.hideFilters") : t("listing.showFilters")}
            </button>
          </div>

          {/* Select filters */}
          <div className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row gap-3 mt-3`}>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
            >
              <option value={allCountriesLabel}>{allCountriesLabel}</option>
              {countries.filter(c => c !== "Tous les pays").map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
            >
              <option value={allSectorsLabel}>{allSectorsLabel}</option>
              {sectors.filter(s => s !== "Tous les secteurs").map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {isFiltered && (
              <button
                onClick={resetFilters}
                className="px-4 py-3 text-sm font-semibold text-orange-500 border border-orange-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("listing.reset")}
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mb-4 md:mb-6">
            {articles.length} {articles.length > 1 ? t("listing.results_plural") : t("listing.results")}
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-64 md:h-72 animate-pulse"></div>
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {articles.map((a) => <ArticleCard key={a.id} article={a as Parameters<typeof ArticleCard>[0]["article"]} />)}
          </div>
        ) : (
          <div className="text-center py-16 md:py-20 text-gray-400">
            <i className="ri-search-line text-5xl mb-4 block"></i>
            <p className="text-lg font-medium">{t("listing.noResults")}</p>
            <p className="text-sm mt-2">{t("listing.noResultsHint")}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
